import React from "react";
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FaRegQuestionCircle } from "react-icons/fa";
import Decimal from "decimal.js";
import { DCFItems } from "../../contexts/constants";
import { useTranslation } from "react-i18next";

// レベルに応じたインデント（px）を取得
const getLevelIndent = (level: number): number => {
  switch (level) {
    case 1: return 0;
    case 2: return 6;
    default: return 0;
  }
};

interface DCFExplanationPopoverProps {
  discountRate: Decimal;
  /** 簡易モードか（true時は営業利益・償却費の説明をサマリー由来の文言に変更） */
  isSimpleMode?: boolean;
}

/**
 * DCF法の項目説明ポップオーバー
 *
 * 使用例:
 * <DCFExplanationPopover discountRate={discountRate} />
 */
export const DCFExplanationPopover: React.FC<DCFExplanationPopoverProps> = ({ discountRate, isSimpleMode = false }) => {
  const { t } = useTranslation();
  const p = "features.InvestmentScreening.Popover.dcfExplanation.desc";
  // DCF法の項目説明データ（discountRateを使用するため関数内で定義）
  const dcfExplanationData = [
    { symbol: "A", item: DCFItems.freeCashFlow, description: "B x (1 - C) + D - E" },
    { symbol: "B", item: DCFItems.operatingProfit, description: isSimpleMode ? t(`${p}.simpleProfit`) : t(`${p}.plItem21`) },
    { symbol: "C", item: DCFItems.taxRate, description: t(`${p}.underConsideration`) },
    { symbol: "D", item: DCFItems.depreciation, description: isSimpleMode ? t(`${p}.simpleDepreciation`) : t(`${p}.plItem14`) },
    { symbol: "E", item: DCFItems.capexAmount, description: t(`${p}.capexScreenInput`) },
    { symbol: "F", item: DCFItems.wacc, description: t(`${p}.discountRateValue`, { rate: discountRate.toFixed(1) }) },
    { symbol: "G", item: DCFItems.presentValue, description: t(`${p}.presentValueFormula`) },
    { symbol: "H", item: DCFItems.netPresentValue, description: t(`${p}.npvDesc`) },
  ];

  return (
    <Popover placement="right">
      <PopoverTrigger>
        <button style={{ background: 'none', border: 'none', padding: 0 }}>
          <FaRegQuestionCircle size="30px" cursor="pointer" />
        </button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="1000px" maxH="600px" overflowY="auto" bgColor="#fffff0">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold">{t('features.InvestmentScreening.Popover.dcfExplanation.title')}</PopoverHeader>
          <PopoverBody>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th w="60px">{t('common.label.symbol')}</Th>
                  <Th w="300px">{t('common.label.item')}</Th>
                  <Th>{t('common.label.description')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {dcfExplanationData.map((row, index) => (
                  <Tr key={index}>
                    <Td>{row.symbol}</Td>
                    <Td
                      fontWeight={row.item.level === 1 ? "bold" : "normal"}
                      pl={getLevelIndent(row.item.level)}
                    >
                      {row.item.name}
                    </Td>
                    <Td>{row.description}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
